import { useEffect, useMemo } from 'react'
import { useRateConvert } from '@web/walter-lib/stream'
import { useConvertConfig } from '~hooks/useConvertConfig'
import { useCommonCMSConfig } from '~api'
import { useBaseCurrency } from '~hooks/useBaseCurrency'
import { usePrevCurrentRatesBatch } from '~hooks/usePrevCurrentRatesBatch'
import { getLastTwoPrices } from '~hooks/useEstimatedBalances'

export const useCustomRates = () => {
    const { data, isValidating: isLoading } = useCommonCMSConfig()
    const { data: convertConfig } = useConvertConfig()
    const rateConvert = useRateConvert()
    const { calculatedBaseCurrency: baseCurrency } = useBaseCurrency()

    const isNewRateService = data?.features?.isNewRateService

    const customRateCurrencies = useMemo(
        () => (data?.customRateCurrencies?.map(({ code }) => code).filter(Boolean) as string[]) ?? [],
        [data?.customRateCurrencies]
    )

    const { ratesData, prevRatesData } = usePrevCurrentRatesBatch({
        isNewRateService: Boolean(customRateCurrencies.length),
        quote: baseCurrency,
        currencies: customRateCurrencies,
    })

    const rates = getLastTwoPrices({ ratesData, prevRatesData })

    useEffect(() => {
        if (isLoading) {
            return
        }

        if (isNewRateService) {
            return
        }

        if (rateConvert) {
            const entries = Object.entries(convertConfig)

            entries.forEach(([from, arrayTo]) => {
                arrayTo?.forEach(to => {
                    rateConvert.setCustomRate(from, to, { open: 1, close: 1 })
                })
            })
        }
    }, [convertConfig, rateConvert, isNewRateService, isLoading])

    useEffect(() => {
        customRateCurrencies.forEach(code => {
            const open = rates?.[code]?.yesterday
            const close = rates?.[code]?.current

            if (rateConvert && open && close) {
                rateConvert.setCustomRate(code, baseCurrency, { open, close })
            }
        })
    }, [customRateCurrencies, rateConvert, baseCurrency, rates])
}
